import HomeBannerAnnotations from "./HomeBannerAnnotations";

// THREE et ses loaders viennent du bundle-three-vendor (chargé avant celui-ci, cf. app/js/ThreeVendor.js) :
// une seule copie de Three.js partagée entre bundle-home/wave/solutions.
const { THREE, GLTFLoader, DRACOLoader, MeshoptDecoder, OrbitControls } = window.ThreeVendor;

const BG_COLOR = 0xf2f3ff;
const CAM_FINAL = new THREE.Vector3(1.661, 1.799, 3.029);
const CAM_START_Y = 4.6;
const INTRO_DURATION = 5.2;

const I_OVERLAY_OUT = [0.0, 0.4];
const I_TERRAIN = [0.5, 2.5];
const I_TOPO_DRAW = [1.8, 3.8];
const I_BUILDINGS = [2.4, 4.2];
const I_ANNOT = [2.8, 4.8];

const S2_EL_START = 38;
const S2_AZ_OFFSET = 12;
const S2_ZOOM_START_MUL = 1.6;
const ORTHO_DIST = 20;
const SCROLL_MAX = 4.0;

// Budget de temps (ms) d'une tranche de préchargement de la scène 2 : au-delà, la
// boucle rend la main au rAF. Génération des nuages de points et upload GPU sont
// ainsi étalés sur plusieurs frames au lieu de geler une frame entière.
const CHUNK_BUDGET_MS = 6;

// Deux renderers WebGL plein écran en MSAA : le coût de rasterisation suit le carré du
// DPR. Plafonné à 1,5 (×0,56 de pixels par rapport à 2 sur écran Retina) : la scène est
// faite de points et de lignes fines sur fond clair, la perte de netteté est imperceptible.
const MAX_PIXEL_RATIO = 1.5;
// DPR de référence sur lequel la taille des points a été calée (anciennement le plafond).
const POINT_SIZE_REFERENCE_DPR = 2;

function getPixelRatio() {
	return Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO);
}

// gl_PointSize s'exprime en pixels physiques : baisser le DPR grossirait les points à l'écran.
// On réduit d'autant la hauteur d'écran passée aux shaders pour conserver leur taille visible.
function getPointScreenHeight() {
	return window.innerHeight * getPixelRatio() / Math.min(window.devicePixelRatio, POINT_SIZE_REFERENCE_DPR);
}

function easeOut(t) {
	return 1 - Math.pow(1 - t, 3);
}
function easeInOut(t) {
	return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
function easeInOutQuart(t) {
	return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
}
function easeInOutQuad(t) {
	return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
function phase(elapsed, start, end) {
	return Math.max(0, Math.min(1, (elapsed - start) / (end - start)));
}

function createDotTexture() {
	const c = document.createElement("canvas");
	c.width = c.height = 64;
	const x = c.getContext("2d");
	x.fillStyle = "#fff";
	x.beginPath();
	x.arc(32, 32, 30, 0, Math.PI * 2);
	x.fill();
	return new THREE.CanvasTexture(c);
}

export default class HomeBannerScene {
	constructor({ canvas1, canvas2, annotCanvas, fadeEl, whiteEl, screen1El, basePath, dracoDecoderPath, reducedMotion }) {
		this.basePath = basePath;
		this.reducedMotion = reducedMotion;
		this.canvas1 = canvas1;
		this.canvas2 = canvas2;
		this.fadeEl = fadeEl;
		this.whiteEl = whiteEl;

		// .glb optimisés : maisons.glb en Draco, terrain.glb/maisons2.glb en Meshopt —
		// un seul loader décodant les deux, réutilisé pour tous les chargements GLTF.
		this.dracoLoader = new DRACOLoader();
		this.dracoLoader.setDecoderPath(dracoDecoderPath);
		this.screen1El = screen1El;
		this.annotations = new HomeBannerAnnotations(annotCanvas);
		this.annotations.resize(window.innerWidth, window.innerHeight);

		this.introPlaying = false;
		this.introStart = 0;
		this.annotReveal = 0;
		this.hotspotReveal = 0;
		this.mx = 0;
		this.my = 0;
		this.scrollRotOffset = 0;

		this.scrollT = 0;
		this.scrollTSmooth = 0;
		this.scrollInitialized = false;

		this.scene2Loaded = false;
		this.scene2Loading = false;
		this.scene2Active = false;
		this.scene2FadeIn = 0;

		this.altitudePoints = [];
		this.altMeshLocalPositions = [];
		this.etiquetteLocalPositions = [];
		this.altMeshLocalPositions2 = [];
		this.altitudePoints2 = [];
		this.scanItems2 = [];
		this.activeScanIdx2 = null;
		this.lastScanIdx2 = null;
		this.scan2Order = null;
		this.scan2OrderIdx = 0;
		this.scanAnimStartMs2 = null;
		this.scan2Speed = 3.0;
		this.scan2RefStep = null;
		this.etiq2LocalPositions = [];
		this.hotspotLocalPos = new THREE.Vector3(0.4726725366978833, 0.4503095624359107, 1.0048503007874532);
		this.hotspotWorldPos = new THREE.Vector3();

		this.clock = new THREE.Clock();

		this._buildScene1();
		this._buildScene2();
	}

	/* ====================== SCÈNE 1 ====================== */
	_buildScene1() {
		const bgColor = new THREE.Color(BG_COLOR);
		this.scene = new THREE.Scene();
		this.scene.background = bgColor;
		this.scene.fog = new THREE.Fog(bgColor, 20.0, 31.0);

		this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
		this.camera.position.copy(CAM_FINAL);

		this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas1, antialias: true });
		this.renderer.setSize(window.innerWidth, window.innerHeight);
		this.renderer.setPixelRatio(getPixelRatio());

		this.controls = new OrbitControls(this.camera, this.renderer.domElement);
		this.controls.target.set(-0.292, 0.285, 0.235);
		this.controls.enableRotate = false;
		this.controls.enableZoom = false;
		this.controls.enablePan = false;
		this.controls.enableDamping = true;

		this.scene.add(new THREE.AmbientLight(0xffffff, 1.35));
		this.sun = new THREE.DirectionalLight(0xffffff, 1.85);
		this.sun.position.set(5, 10, 5);
		this.scene.add(this.sun);
		{
			const az = (233 * Math.PI) / 180;
			const el = (90 * Math.PI) / 180;
			this.sun.position.set(Math.cos(el) * Math.sin(az) * 10, Math.sin(el) * 10, Math.cos(el) * Math.cos(az) * 10);
		}

		this.topoUniforms = {
			uColor: { value: new THREE.Color(0x6289ff) },
			uSpacing: { value: 94.0 },
			uLineWidth: { value: 0.1 },
			uReveal: { value: 0.0 },
			uTopoRevealDist: { value: 0.0 },
		};
		this.waveUniforms = {
			uTime: { value: 0 },
			uBaseColor: { value: new THREE.Color(0x0033ff) },
			uWaveColor: { value: new THREE.Color(0x85ffe7) },
			uWaveSpeed: { value: 0.5 },
			uWaveSize: { value: 0.8 },
			uPointSize: { value: 0.005 },
			uWaveOrigin0: { value: new THREE.Vector2(0, 0) },
			uWaveOrigin1: { value: new THREE.Vector2(0, 0) },
			uWaveOrigin2: { value: new THREE.Vector2(0, 0) },
			uWavePhase0: { value: 0.0 },
			uWavePhase1: { value: 2.1 },
			uWavePhase2: { value: 4.3 },
			uWaveWeight0: { value: 1.0 },
			uWaveWeight1: { value: 0.0 },
			uWaveWeight2: { value: 0.0 },
			uScreenHeight: { value: getPointScreenHeight() },
			uRevealRadius: { value: -1.0 },
			uRevealOrigin: { value: new THREE.Vector2(0, 0) },
			uTerrainReveal: { value: 0.0 },
			uWaveSizeBoost: { value: 1.5 },
		};

		this.topoMaterial = new THREE.ShaderMaterial({
			uniforms: { ...this.topoUniforms, ...THREE.UniformsLib["fog"] },
			vertexShader: `
				#include <fog_pars_vertex>
				uniform float uReveal;
				varying float vY;
				varying float vViewDist;
				void main() {
					vec3 pos = position;
					pos.y *= uReveal;
					vY = pos.y;
					vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
					vViewDist = length(mvPosition.xyz);
					gl_Position = projectionMatrix * mvPosition;
					#include <fog_vertex>
				}
			`,
			fragmentShader: `
				#include <fog_pars_fragment>
				uniform vec3 uColor;
				uniform float uSpacing;
				uniform float uLineWidth;
				uniform float uTopoRevealDist;
				varying float vY;
				varying float vViewDist;
				void main() {
					float drawEdge = smoothstep(uTopoRevealDist, uTopoRevealDist - 0.6, vViewDist);
					if (drawEdge < 0.01) discard;
					float coord = vY * uSpacing;
					float fraction = fract(coord);
					float dist = abs(fraction - 0.5);
					float delta = fwidth(coord);
					float line = smoothstep(delta * (uLineWidth + 0.5), delta * (uLineWidth - 0.5), dist);
					if (line < 0.1) discard;
					gl_FragColor = vec4(uColor, line * drawEdge);
					#include <fog_fragment>
				}
			`,
			transparent: true,
			side: THREE.DoubleSide,
			extensions: { derivatives: true },
		});

		this.parallaxGroup = new THREE.Group();
		this.scene.add(this.parallaxGroup);
		this.housesGroup = new THREE.Group();
		this.parallaxGroup.add(this.housesGroup);

		this.terrainCollisionMesh = null;
		this.pointsObject = null;
	}

	/* ====================== SCÈNE 2 ====================== */
	_buildScene2() {
		const bgColor2 = new THREE.Color(BG_COLOR);
		this.scene2 = new THREE.Scene();
		this.scene2.background = bgColor2;
		this.scene2.fog = new THREE.Fog(bgColor2, 20.0, 31.0);

		const zoom = 3.0;
		const aspect = window.innerWidth / window.innerHeight;
		this.camera2 = new THREE.OrthographicCamera(-zoom * aspect, zoom * aspect, zoom, -zoom, 0.01, 1000);

		this.renderer2 = new THREE.WebGLRenderer({ canvas: this.canvas2, antialias: true });
		this.renderer2.setSize(window.innerWidth, window.innerHeight);
		this.renderer2.setPixelRatio(getPixelRatio());
		this.renderer2.outputColorSpace = THREE.SRGBColorSpace;
		this.renderer2.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer2.toneMappingExposure = 1.0;

		this._updateOrthoCamera();

		this.scene2.add(new THREE.AmbientLight(0xffffff, 1.45));
		this.sun2 = new THREE.DirectionalLight(0xffffff, 3.45);
		this.sun2.position.set(5, 10, 5);
		this.scene2.add(this.sun2);
		{
			const az = (322 * Math.PI) / 180;
			const el = (68 * Math.PI) / 180;
			this.sun2.position.set(Math.cos(el) * Math.sin(az) * 10, Math.sin(el) * 10, Math.cos(el) * Math.cos(az) * 10);
		}

		this.waveUniforms2 = {
			uTime: { value: 0 },
			uBaseColor: { value: new THREE.Color(0x0033ff) },
			uWaveColor: { value: new THREE.Color(0x85ffe7) },
			uWaveSpeed: { value: 0.5 },
			uWaveSize: { value: 0.8 },
			uPointSize: { value: 0.055 },
			uWaveOrigin0: { value: new THREE.Vector2(0, 0) },
			uWaveOrigin1: { value: new THREE.Vector2(0, 0) },
			uWaveOrigin2: { value: new THREE.Vector2(0, 0) },
			uWavePhase0: { value: 0.0 },
			uWavePhase1: { value: 2.1 },
			uWavePhase2: { value: 4.3 },
			uWaveWeight0: { value: 0.0 },
			uWaveWeight1: { value: 0.0 },
			uWaveWeight2: { value: 0.0 },
			uScreenHeight: { value: getPointScreenHeight() },
			uRevealRadius: { value: -1.0 },
			uRevealOrigin: { value: new THREE.Vector2(0, 0) },
			uTerrainReveal: { value: 0.0 },
			uWaveSizeBoost: { value: 0.0 },
			uSweepColor: { value: new THREE.Color(0x12ebc7) },
			uSweepPos: { value: 0.0 },
			uSweepWidth: { value: 1.8 },
			uSweepStrength: { value: 1.0 },
		};
		this.topoUniforms2 = {
			uColor: { value: new THREE.Color(0x6289ff) },
			uSpacing: { value: 94.0 },
			uLineWidth: { value: 0.1 },
			uReveal: { value: 0.0 },
			uTopoRevealDist: { value: 0.0 },
		};

		this.parallaxGroup2 = new THREE.Group();
		this.scene2.add(this.parallaxGroup2);
		this.housesGroup2 = new THREE.Group();
		this.parallaxGroup2.add(this.housesGroup2);

		this.terrainCollisionMesh2 = null;
		this.pointsObject2 = null;
		this.scene2ShadowMesh = null;
	}

	_updateOrthoCamera() {
		const zoom = 1.7;
		const az = (45.0 * Math.PI) / 180;
		const el = (30.0 * Math.PI) / 180;
		const target2 = new THREE.Vector3(-0.01, 0, 0);
		const dx = Math.cos(el) * Math.sin(az);
		const dy = Math.sin(el);
		const dz = Math.cos(el) * Math.cos(az);
		this.camera2.position.set(target2.x + dx * ORTHO_DIST, target2.y + dy * ORTHO_DIST, target2.z + dz * ORTHO_DIST);
		this.camera2.lookAt(target2);

		const aspect = window.innerWidth / window.innerHeight;
		this.camera2.left = -zoom * aspect;
		this.camera2.right = zoom * aspect;
		this.camera2.top = zoom;
		this.camera2.bottom = -zoom;
		this.camera2.updateProjectionMatrix();
	}

	_applyOrthoWithEl(elDeg, azOffsetDeg = 0, zoomMul = 1) {
		const zoom = 1.7 * zoomMul;
		const az = ((45.0 + azOffsetDeg) * Math.PI) / 180;
		const el = (elDeg * Math.PI) / 180;
		const tgt = new THREE.Vector3(-0.01, 0, 0);
		this.camera2.position.set(
			tgt.x + Math.cos(el) * Math.sin(az) * ORTHO_DIST,
			tgt.y + Math.sin(el) * ORTHO_DIST,
			tgt.z + Math.cos(el) * Math.cos(az) * ORTHO_DIST
		);
		this.camera2.lookAt(tgt);
		const aspect = window.innerWidth / window.innerHeight;
		this.camera2.left = -zoom * aspect;
		this.camera2.right = zoom * aspect;
		this.camera2.top = zoom;
		this.camera2.bottom = -zoom;
		this.camera2.updateProjectionMatrix();
	}

	_makeGLTFLoader() {
		const loader = new GLTFLoader();
		loader.setDRACOLoader(this.dracoLoader);
		loader.setMeshoptDecoder(MeshoptDecoder);
		return loader;
	}

	/* ====================== CHARGEMENT ====================== */
	async load() {
		const loader = this._makeGLTFLoader();
		const [tData, mData] = await Promise.all([
			loader.loadAsync(`${this.basePath}/terrain.glb`),
			loader.loadAsync(`${this.basePath}/maisons.glb`),
		]);

		const tCol = tData.scene.clone();
		tCol.traverse((c) => {
			if (c.isMesh) {
				c.geometry.computeBoundsTree();
				c.visible = false;
			}
		});
		this.parallaxGroup.add(tCol);
		this.terrainCollisionMesh = tCol;

		this._extractAltMeshes(tData.scene);
		this._extractEtiquetteMeshes(tData.scene);
		this._extractEtiquetteMeshes(mData.scene);
		this._extractOndeMeshes(tData.scene);
		this._detectHotspot(tData.scene);
		this._detectHotspot(mData.scene);

		tData.scene.traverse((c) => {
			if (c.isMesh && c.visible !== false) c.material = this.topoMaterial;
		});
		this.parallaxGroup.add(tData.scene);
		this.housesGroup.add(mData.scene);
		this._initHotspotFallback(this.housesGroup);

		if (this.hotspotLocalPos) {
			const hx = this.hotspotLocalPos.x;
			const hz = this.hotspotLocalPos.z;
			this.waveUniforms.uRevealOrigin.value.set(hx, hz);
			this.waveUniforms.uWaveOrigin0.value.set(hx, hz);
			this.waveUniforms.uWaveOrigin1.value.set(hx, hz);
			this.waveUniforms.uWaveOrigin2.value.set(hx, hz);
			this.waveUniforms.uWaveWeight0.value = 1.0;
			this.waveUniforms.uWaveWeight1.value = 0.0;
			this.waveUniforms.uWaveWeight2.value = 0.0;
		}

		this._updateHouses();
		this._updateFade();
		this._regenerateGrid();
		this._updateHotspotWorldPos();
		this._initScrollTargets();
	}

	_updateFade() {
		if (!this.fadeEl) return;
		this.fadeEl.style.background = "linear-gradient(to bottom, #F2F3FF 0%, transparent 30%)";
		this.fadeEl.style.opacity = "1";
	}

	// Rend la main au navigateur pour une frame.
	_nextFrame() {
		return new Promise((resolve) => requestAnimationFrame(() => resolve()));
	}

	// À appeler dans les boucles longues du préchargement : cède la frame si le
	// budget de la tranche est épuisé, et renvoie l'échéance suivante.
	async _yieldIfOverBudget(deadline) {
		if (performance.now() < deadline) return deadline;
		await this._nextFrame();
		return performance.now() + CHUNK_BUDGET_MS;
	}

	// Préchargement en tâche de fond, lancé dès que la scène 1 est prête (donc
	// pendant l'intro) : le coût de la scène 2 sort ainsi complètement du scroll.
	// Sans await — en cas d'échec réseau on relâche le verrou pour que le
	// déclenchement au scroll puisse retenter.
	preloadScene2() {
		// En reduced-motion le scrub est désactivé et la scène reste figée sur la fin
		// de l'intro (cf. _snapIntroToEnd) : la scène 2 ne sera jamais affichée.
		if (this.reducedMotion) return;

		this.loadScene2().catch((err) => {
			this.scene2Loading = false;
			console.warn("HomeBannerScene : préchargement de la scène 2 interrompu", err);
		});
	}

	async loadScene2() {
		if (this.scene2Loading || this.scene2Loaded) return;
		this.scene2Loading = true;
		const loader2 = this._makeGLTFLoader();
		const [tData2, mData2] = await Promise.all([
			loader2.loadAsync(`${this.basePath}/terrain2.glb`),
			loader2.loadAsync(`${this.basePath}/maisons2.glb`),
		]);

		// Pas de computeBoundsTree() ici : le BVH ne sert qu'au raycast de
		// _regenerateGrid2(), qui ne le construit que si le terrain a du relief.
		const tCol2 = tData2.scene.clone();
		tCol2.traverse((c) => {
			if (c.isMesh) c.visible = false;
		});
		this.parallaxGroup2.add(tCol2);
		this.terrainCollisionMesh2 = tCol2;

		this._extractAltMeshes2(tData2.scene);

		const bbox2 = new THREE.Box3().setFromObject(tCol2);
		const center2 = new THREE.Vector3();
		bbox2.getCenter(center2);
		this.waveUniforms2.uRevealOrigin.value.set(center2.x, center2.z);

		tData2.scene.traverse((c) => {
			if (c.isMesh) c.visible = false;
		});
		this.parallaxGroup2.add(tData2.scene);

		mData2.scene.traverse((c) => {
			if (c.isMesh && c.material) {
				c.material = Array.isArray(c.material)
					? c.material.map((m) => {
							const n = m.clone();
							n.fog = true;
							return n;
					  })
					: ((c.material = c.material.clone()), (c.material.fog = true), c.material);
			}
		});
		this.housesGroup2.add(mData2.scene);

		this.parallaxGroup2.updateMatrixWorld(true);
		const scanOrder = ["scan1", "scan2", "scan3"];

		{
			let refMaxSide = 0;
			scanOrder.forEach((sn) => {
				mData2.scene.traverse((node) => {
					if (node.isMesh && node.name.toLowerCase().startsWith(sn)) {
						node.updateMatrixWorld(true);
						const sz = new THREE.Box3().setFromObject(node).getSize(new THREE.Vector3());
						refMaxSide = Math.max(refMaxSide, sz.x, sz.y, sz.z);
					}
				});
			});
			const dtRef = 280;
			this.scan2RefStep = refMaxSide > 0 ? refMaxSide / dtRef : null;
		}

		for (const scanName of scanOrder) {
			let found = null;
			mData2.scene.traverse((node) => {
				if (!found && node.isMesh && node.name.toLowerCase().startsWith(scanName)) found = node;
			});
			if (found) {
				found.updateMatrixWorld(true);
				this.scanItems2.push(await this._buildScanItem(found));
			}
		}

		const shadowTex = await new Promise((resolve, reject) => {
			new THREE.TextureLoader().load(`${this.basePath}/ombres.png`, resolve, undefined, reject);
		});
		shadowTex.colorSpace = THREE.SRGBColorSpace;
		shadowTex.anisotropy = 8;
		const tBbox = new THREE.Box3().setFromObject(this.terrainCollisionMesh2);
		const tSize = tBbox.getSize(new THREE.Vector3());
		const tCenter = tBbox.getCenter(new THREE.Vector3());
		const shadowMat = new THREE.MeshBasicMaterial({
			map: shadowTex,
			transparent: true,
			opacity: 0.6,
			blending: THREE.NormalBlending,
			depthWrite: false,
			fog: true,
		});
		const shadowMesh = new THREE.Mesh(new THREE.PlaneGeometry(tSize.x, tSize.z), shadowMat);
		shadowMesh.rotation.set(-Math.PI / 2, 0, Math.PI / 2);
		shadowMesh.position.set(tCenter.x, tBbox.min.y + 0.005, tCenter.z);
		shadowMesh.renderOrder = 1;
		this.parallaxGroup2.add(shadowMesh);
		this.scene2ShadowMesh = shadowMesh;

		this._regenerateGrid2();
		this._pickAltitudePoints2();
		this._activateScene2();
		await this._nextFrame();

		// Compilation des programmes GLSL puis upload GPU des buffers avant de
		// déclarer la scène prête. Sans ça, le tout premier renderer2.render()
		// faisait les deux en synchrone au milieu du scroll : ~430 ms de main thread
		// gelé, d'où le hoquet au premier passage entre les deux scènes. compileAsync
		// s'appuie sur KHR_parallel_shader_compile et laisse le driver travailler en
		// parallèle (~15 ms d'attente au lieu de 350).
		await this.renderer2.compileAsync(this.scene2, this.camera2);
		await this._warmUpScene2();

		this.scene2Loaded = true;
		this.scene2Loading = false;
	}

	// Upload GPU des buffers de la scène 2 avant tout affichage. Un render() de la
	// scène complète pousserait les ~5 Mo d'attributs de points d'un coup (~80 ms
	// de frame) : les nuages sont révélés un par un, une frame chacun. Le canvas2
	// est encore à opacity 0 — rien n'est visible pendant l'opération.
	async _warmUpScene2() {
		const clouds = [this.pointsObject2, ...this.scanItems2.map((item) => item.points)].filter(Boolean);
		const wasVisible = clouds.map((o) => o.visible);
		const wasCulled = clouds.map((o) => o.frustumCulled);

		// Frustum culling neutralisé le temps du warm-up : un nuage hors cadre ne
		// serait pas dessiné, donc pas uploadé, et le coût retomberait sur la frame
		// où il entre à l'écran.
		clouds.forEach((o) => {
			o.visible = false;
			o.frustumCulled = false;
		});

		this.renderer2.render(this.scene2, this.camera2);

		for (let i = 0; i < clouds.length; i++) {
			await this._nextFrame();
			clouds[i].visible = wasVisible[i];
			this.renderer2.render(this.scene2, this.camera2);
		}

		clouds.forEach((o, i) => (o.frustumCulled = wasCulled[i]));
	}

	/* ====================== EXTRACTION DES MARQUEURS GLB ====================== */
	_extractAltMeshes(terrainScene) {
		const found = [];
		terrainScene.traverse((node) => {
			if (/^alt\d*$/i.test(node.name)) {
				node.visible = false;
				if (node.isMesh && node.material) {
					node.material = node.material.clone();
					node.material.visible = false;
				}
				const wp = new THREE.Vector3();
				node.getWorldPosition(wp);
				found.push({ localPos: this.parallaxGroup.worldToLocal(wp.clone()), name: node.name });
			}
		});
		found.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
		this.altMeshLocalPositions = found.map((f) => f.localPos);
	}

	_extractOndeMeshes(terrainScene) {
		const found = [];
		terrainScene.traverse((node) => {
			if (/^onde\d*$/i.test(node.name)) {
				node.visible = false;
				if (node.isMesh && node.material) {
					node.material = node.material.clone();
					node.material.visible = false;
				}
				const wp = new THREE.Vector3();
				node.getWorldPosition(wp);
				found.push({ localPos: this.parallaxGroup.worldToLocal(wp.clone()), name: node.name });
			}
		});
		found.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
		const ondeMeshLocalPositions = found.map((f) => f.localPos);
		const n = Math.min(ondeMeshLocalPositions.length, 3);
		if (n > 0) {
			const phases = [Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2];
			const origins = [
				ondeMeshLocalPositions[0] || new THREE.Vector3(),
				ondeMeshLocalPositions[1] || new THREE.Vector3(),
				ondeMeshLocalPositions[2] || new THREE.Vector3(),
			];
			this.waveUniforms.uWaveOrigin0.value.set(origins[0].x, origins[0].z);
			this.waveUniforms.uWaveOrigin1.value.set(origins[1].x, origins[1].z);
			this.waveUniforms.uWaveOrigin2.value.set(origins[2].x, origins[2].z);
			this.waveUniforms.uWavePhase0.value = phases[0];
			this.waveUniforms.uWavePhase1.value = phases[1];
			this.waveUniforms.uWavePhase2.value = phases[2];
			this.waveUniforms.uWaveWeight0.value = n >= 1 ? 1.0 : 0.0;
			this.waveUniforms.uWaveWeight1.value = n >= 2 ? 1.0 : 0.0;
			this.waveUniforms.uWaveWeight2.value = n >= 3 ? 1.0 : 0.0;
		}
	}

	_extractEtiquetteMeshes(gltfScene) {
		gltfScene.traverse((node) => {
			if (/^etiquette\d*$/i.test(node.name)) {
				node.visible = false;
				if (node.isMesh && node.material) {
					node.material = node.material.clone();
					node.material.visible = false;
				}
				const wp = new THREE.Vector3();
				node.getWorldPosition(wp);
				const lp = this.parallaxGroup.worldToLocal(wp.clone());
				if (this.etiquetteLocalPositions.every((e) => e.name !== node.name)) {
					this.etiquetteLocalPositions.push({ localPos: lp, name: node.name });
				}
			}
		});
		this.etiquetteLocalPositions.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
	}

	_extractAltMeshes2(terrainScene) {
		const found = [];
		terrainScene.traverse((node) => {
			if (/^alt\d*$/i.test(node.name)) {
				node.visible = false;
				if (node.isMesh && node.material) {
					node.material = node.material.clone();
					node.material.visible = false;
				}
				const wp = new THREE.Vector3();
				node.getWorldPosition(wp);
				const lp = this.parallaxGroup2.worldToLocal(wp.clone());
				found.push({ localPos: lp, name: node.name });
			}
		});
		found.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
		this.altMeshLocalPositions2 = found.map((f) => f.localPos);
	}

	_detectHotspot(sceneNode) {
		sceneNode.traverse((node) => {
			if (/^(zone1|hotspot)$/i.test(node.name) && !this.hotspotLocalPos) {
				node.visible = false;
				const wp = new THREE.Vector3();
				node.getWorldPosition(wp);
				this.hotspotLocalPos = this.parallaxGroup.worldToLocal(wp.clone());
			}
		});
	}

	_initHotspotFallback(housesGrp) {
		if (this.hotspotLocalPos) return;
		const bbox = new THREE.Box3().setFromObject(housesGrp);
		if (!bbox.isEmpty()) {
			const center = new THREE.Vector3();
			bbox.getCenter(center);
			this.hotspotLocalPos = this.parallaxGroup.worldToLocal(center);
		}
	}

	_updateHotspotWorldPos() {
		if (!this.hotspotLocalPos) return;
		this.hotspotWorldPos.copy(this.hotspotLocalPos);
		this.parallaxGroup.localToWorld(this.hotspotWorldPos);
	}

	_updateHouses() {
		const color = new THREE.Color("#b3c6ff");
		this.housesGroup.traverse((c) => {
			if (c.isMesh) c.material = new THREE.MeshStandardMaterial({ color, transparent: true, opacity: 1, fog: true });
		});
	}

	/* ====================== GRILLES DE POINTS ====================== */
	_regenerateGrid() {
		if (!this.terrainCollisionMesh) return;
		if (this.pointsObject) this.parallaxGroup.remove(this.pointsObject);

		const density = 202;
		const bbox = new THREE.Box3().setFromObject(this.terrainCollisionMesh);
		const size = bbox.getSize(new THREE.Vector3());
		const nx = density;
		const nz = Math.round(density * (size.z / size.x));
		const stepX = size.x / nx;
		const stepZ = size.z / nz;
		const positions = [];
		const raycaster = new THREE.Raycaster();
		const down = new THREE.Vector3(0, -1, 0);

		for (let i = 0; i <= nx; i++) {
			for (let j = 0; j <= nz; j++) {
				const x = bbox.min.x + i * stepX;
				const z = bbox.min.z + j * stepZ;
				raycaster.set(new THREE.Vector3(x, bbox.max.y + 1, z), down);
				const hits = raycaster.intersectObject(this.terrainCollisionMesh, true);
				if (hits.length > 0) positions.push(hits[0].point.x, hits[0].point.y, hits[0].point.z);
			}
		}

		const geom = new THREE.BufferGeometry();
		geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
		this.pointsObject = new THREE.Points(
			geom,
			new THREE.ShaderMaterial({
				uniforms: { ...this.waveUniforms, ...THREE.UniformsLib["fog"], uTexture: { value: createDotTexture() } },
				vertexShader: `
					#include <fog_pars_vertex>
					uniform float uTime; uniform float uWaveSpeed; uniform float uWaveSize; uniform float uPointSize;
					uniform vec2 uWaveOrigin0; uniform vec2 uWaveOrigin1; uniform vec2 uWaveOrigin2;
					uniform float uWavePhase0; uniform float uWavePhase1; uniform float uWavePhase2;
					uniform float uWaveWeight0; uniform float uWaveWeight1; uniform float uWaveWeight2;
					uniform float uScreenHeight;
					uniform float uRevealRadius;
					uniform vec2  uRevealOrigin;
					uniform float uTerrainReveal;
					uniform float uWaveSizeBoost;
					varying float vWave;
					varying float vAlpha;
					void main() {
						vec3 pos = position;
						pos.y *= uTerrainReveal;
						float t = uTime * uWaveSpeed * 3.0;
						float totalW = uWaveWeight0 + uWaveWeight1 + uWaveWeight2;
						float w = 0.0;
						w += uWaveWeight0 * sin(distance(pos.xz, uWaveOrigin0) * uWaveSize - t + uWavePhase0);
						w += uWaveWeight1 * sin(distance(pos.xz, uWaveOrigin1) * uWaveSize - t + uWavePhase1);
						w += uWaveWeight2 * sin(distance(pos.xz, uWaveOrigin2) * uWaveSize - t + uWavePhase2);
						w /= max(totalW, 1.0);
						vWave = smoothstep(0.3, 1.0, w);
						float d = distance(pos.xz, uRevealOrigin);
						vAlpha = smoothstep(0.0, 0.35, uRevealRadius - d);
						vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
						float sizeMultiplier = 1.0 + uWaveSizeBoost * vWave;
						gl_PointSize = uPointSize * sizeMultiplier * (uScreenHeight / -mvPosition.z);
						gl_Position = projectionMatrix * mvPosition;
						#include <fog_vertex>
					}
				`,
				fragmentShader: `
					#include <fog_pars_fragment>
					uniform vec3 uBaseColor; uniform vec3 uWaveColor; uniform sampler2D uTexture;
					varying float vWave;
					varying float vAlpha;
					void main() {
						vec3 color = mix(uBaseColor, uWaveColor, vWave);
						vec4 tex = texture2D(uTexture, gl_PointCoord);
						if (tex.a < 0.1) discard;
						gl_FragColor = vec4(color, tex.a * vAlpha);
						#include <fog_fragment>
					}
				`,
				transparent: true,
				depthWrite: false,
			})
		);
		this.parallaxGroup.add(this.pointsObject);
		this._pickAltitudePoints();
	}

	_pickAltitudePoints() {
		if (this.altMeshLocalPositions.length === 0) return;
		let minY = Infinity;
		let maxY = -Infinity;
		this.altMeshLocalPositions.forEach((p) => {
			if (p.y < minY) minY = p.y;
			if (p.y > maxY) maxY = p.y;
		});
		if (this.terrainCollisionMesh) {
			const bbox = new THREE.Box3().setFromObject(this.terrainCollisionMesh);
			minY = Math.min(minY, bbox.min.y);
			maxY = Math.max(maxY, bbox.max.y);
		}
		const yRange = maxY - minY || 1;
		const lineHeights = [28, 38, 50, 34, 60, 42, 26, 55, 32, 46, 44, 30];

		const indices = this.altMeshLocalPositions.map((_, i) => i);
		for (let i = indices.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[indices[i], indices[j]] = [indices[j], indices[i]];
		}
		this.altitudePoints = this.altMeshLocalPositions.map((localP, i) => {
			const worldPos = localP.clone();
			this.parallaxGroup.localToWorld(worldPos);
			const baseAlt = ((localP.y - minY) / yRange) * 650 + 180;
			return {
				localPos: localP,
				worldPos,
				baseAlt,
				phase: (i / this.altMeshLocalPositions.length) * Math.PI * 2 + i * 0.37,
				lineH: lineHeights[i % lineHeights.length],
				side: i % 2 === 0 ? 1 : -1,
				revealOrder: indices.indexOf(i) / Math.max(1, this.altMeshLocalPositions.length - 1),
				alpha: 0,
				lineProgress: 0,
			};
		});
	}

	_updateAltWorldPositions() {
		this.altitudePoints.forEach((pt) => {
			const w = pt.localPos.clone();
			this.parallaxGroup.localToWorld(w);
			pt.worldPos.copy(w);
		});
	}

	_regenerateGrid2() {
		if (!this.terrainCollisionMesh2) return;
		if (this.pointsObject2) this.parallaxGroup2.remove(this.pointsObject2);

		const density = 249;
		const bbox = new THREE.Box3().setFromObject(this.terrainCollisionMesh2);
		const size = bbox.getSize(new THREE.Vector3());
		const nx = density;
		const nz = Math.round(density * (size.z / size.x));
		const stepX = size.x / nx;
		const stepZ = size.z / nz;
		const positions = [];

		// terrain2.glb est un simple plan horizontal (2 triangles) : les 62 500 rayons
		// retombaient tous sur la même altitude, pour 40 ms de calcul. La grille est
		// donc générée directement. Le raycast (et le BVH qu'il exige) ne sert que si
		// le terrain a réellement du relief — cas conservé si l'asset change.
		if (size.y < 1e-4) {
			const y = bbox.min.y;
			for (let i = 0; i <= nx; i++) {
				for (let j = 0; j <= nz; j++) {
					positions.push(bbox.min.x + i * stepX, y, bbox.min.z + j * stepZ);
				}
			}
		} else {
			this.terrainCollisionMesh2.traverse((c) => {
				if (c.isMesh && c.geometry.computeBoundsTree && !c.geometry.boundsTree) c.geometry.computeBoundsTree();
			});
			const raycaster = new THREE.Raycaster();
			const down = new THREE.Vector3(0, -1, 0);
			const origin = new THREE.Vector3();

			for (let i = 0; i <= nx; i++) {
				for (let j = 0; j <= nz; j++) {
					origin.set(bbox.min.x + i * stepX, bbox.max.y + 1, bbox.min.z + j * stepZ);
					raycaster.set(origin, down);
					const hits = raycaster.intersectObject(this.terrainCollisionMesh2, true);
					if (hits.length > 0) positions.push(hits[0].point.x, hits[0].point.y, hits[0].point.z);
				}
			}
		}

		const geom = new THREE.BufferGeometry();
		geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
		this.pointsObject2 = new THREE.Points(
			geom,
			new THREE.ShaderMaterial({
				uniforms: { ...this.waveUniforms2, ...THREE.UniformsLib["fog"], uTexture: { value: createDotTexture() } },
				vertexShader: `
					#include <fog_pars_vertex>
					uniform float uPointSize;
					uniform float uScreenHeight;
					uniform float uTerrainReveal;
					uniform float uSweepPos;
					uniform float uSweepWidth;
					uniform float uSweepStrength;
					varying float vSweep;
					void main() {
						vec3 pos = position;
						pos.y *= uTerrainReveal;
						float proj = (pos.x - pos.z) * 0.7071;
						float d = abs(proj - uSweepPos);
						float sweep = (1.0 - smoothstep(0.0, uSweepWidth, d)) * uSweepStrength;
						vSweep = sweep;
						vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
						gl_PointSize = uPointSize * (1.0 + sweep * 0.8) * (uScreenHeight / -mvPosition.z);
						gl_Position = projectionMatrix * mvPosition;
						#include <fog_vertex>
					}
				`,
				fragmentShader: `
					#include <fog_pars_fragment>
					uniform vec3 uBaseColor;
					uniform vec3 uSweepColor;
					uniform sampler2D uTexture;
					varying float vSweep;
					void main() {
						vec4 tex = texture2D(uTexture, gl_PointCoord);
						if (tex.a < 0.1) discard;
						vec3 col = mix(uBaseColor, uSweepColor, clamp(vSweep * 1.4, 0.0, 1.0));
						gl_FragColor = vec4(col, tex.a);
						#include <fog_fragment>
					}
				`,
				transparent: true,
				depthWrite: false,
			})
		);
		this.parallaxGroup2.add(this.pointsObject2);
	}

	_pickAltitudePoints2() {
		if (this.altMeshLocalPositions2.length === 0) return;
		let minY = Infinity;
		let maxY = -Infinity;
		this.altMeshLocalPositions2.forEach((p) => {
			if (p.y < minY) minY = p.y;
			if (p.y > maxY) maxY = p.y;
		});
		if (this.terrainCollisionMesh2) {
			const bbox = new THREE.Box3().setFromObject(this.terrainCollisionMesh2);
			minY = Math.min(minY, bbox.min.y);
			maxY = Math.max(maxY, bbox.max.y);
		}
		const yRange = maxY - minY || 1;
		const lineHeights = [28, 38, 50, 34, 60, 42, 26, 55, 32, 46, 44, 30];
		this.altitudePoints2 = this.altMeshLocalPositions2.map((localP, i) => {
			const worldPos = localP.clone();
			this.parallaxGroup2.localToWorld(worldPos);
			const baseAlt = ((localP.y - minY) / yRange) * 650 + 180;
			return {
				localPos: localP,
				worldPos,
				baseAlt,
				phase: (i / this.altMeshLocalPositions2.length) * Math.PI * 2 + i * 0.37,
				lineH: lineHeights[i % lineHeights.length],
				side: i % 2 === 0 ? 1 : -1,
			};
		});
	}

	/* ====================== SCAN DES BÂTIMENTS (SCÈNE 2) ====================== */
	// Génère le nuage de points de scan d'un bâtiment (~110 000 points en moyenne).
	// Les boucles externes cèdent la frame dès que CHUNK_BUDGET_MS est dépassé :
	// les ~110 ms de calcul des trois bâtiments s'étalent alors sur une vingtaine
	// de frames au lieu d'en bloquer une seule. Rien ne touche parallaxGroup2 entre
	// deux tranches (render() ne l'anime qu'une fois scene2Loaded), donc la matrice
	// monde reste valide d'un bout à l'autre.
	async _buildScanItem(mesh) {
		mesh.updateMatrixWorld(true);
		this.parallaxGroup2.updateMatrixWorld(true);
		let deadline = performance.now() + CHUNK_BUDGET_MS;

		const DENSITY_TARGET = 200;
		const POINT_SIZE = 11;
		const scanBboxWorld = new THREE.Box3().setFromObject(mesh);
		const bMin = scanBboxWorld.min;
		const bMax = scanBboxWorld.max;
		const bSize = scanBboxWorld.getSize(new THREE.Vector3());

		const positions = [];
		const normals = [];
		const randoms = [];
		const STEP = this.scan2RefStep !== null ? this.scan2RefStep : Math.max(bSize.x, bSize.y, bSize.z) / DENSITY_TARGET;
		const NX = Math.max(2, Math.round(bSize.x / STEP));
		const NY = Math.max(2, Math.round(bSize.y / STEP));
		const NZ = Math.max(2, Math.round(bSize.z / STEP));

		if (mesh.geometry && mesh.geometry.computeBoundsTree && !mesh.geometry.boundsTree) {
			mesh.geometry.computeBoundsTree();
		}
		const _ray = new THREE.Raycaster();
		const _down = new THREE.Vector3(0, -1, 0);
		const _orig = new THREE.Vector3();
		const _rayTop = bMax.y + bSize.y * 0.05;
		const volStart = positions.length;

		const roofArr = new Float32Array((NX + 1) * (NZ + 1)).fill(NaN);
		const colIdx = (ix, iz) => ix * (NZ + 1) + iz;
		for (let ix = 0; ix <= NX; ix++) {
			deadline = await this._yieldIfOverBudget(deadline);
			for (let iz = 0; iz <= NZ; iz++) {
				_orig.set(bMin.x + (ix / NX) * bSize.x, _rayTop, bMin.z + (iz / NZ) * bSize.z);
				_ray.set(_orig, _down);
				const hits = _ray.intersectObject(mesh, true);
				if (hits.length) roofArr[colIdx(ix, iz)] = hits[0].point.y;
			}
		}
		const pushPt = (x, y, z) => {
			positions.push(
				x + (Math.random() - 0.5) * STEP * 0.9,
				y + (Math.random() - 0.5) * STEP * 0.9,
				z + (Math.random() - 0.5) * STEP * 0.9
			);
			normals.push(0, 1, 0);
			randoms.push(Math.random());
		};

		const OUT = STEP * 1.3;
		const UP = STEP * 0.6;
		const fcx = (bMin.x + bMax.x) / 2;
		const fcz = (bMin.z + bMax.z) / 2;

		for (let ix = 0; ix <= NX; ix++) {
			deadline = await this._yieldIfOverBudget(deadline);
			for (let iz = 0; iz <= NZ; iz++) {
				const roofY = roofArr[colIdx(ix, iz)];
				if (isNaN(roofY)) continue;
				const cx = bMin.x + (ix / NX) * bSize.x;
				const cz = bMin.z + (iz / NZ) * bSize.z;
				pushPt(cx, roofY + UP, cz);

				const isPerim =
					ix === 0 ||
					ix === NX ||
					iz === 0 ||
					iz === NZ ||
					isNaN(roofArr[colIdx(ix - 1, iz)]) ||
					isNaN(roofArr[colIdx(ix + 1, iz)]) ||
					isNaN(roofArr[colIdx(ix, iz - 1)]) ||
					isNaN(roofArr[colIdx(ix, iz + 1)]);
				if (isPerim) {
					let dx = cx - fcx;
					let dz = cz - fcz;
					const dl = Math.hypot(dx, dz) || 1;
					dx = (dx / dl) * OUT;
					dz = (dz / dl) * OUT;
					for (let y = bMin.y; y < roofY - STEP * 0.5; y += STEP) pushPt(cx + dx, y, cz + dz);
				}
			}
		}

		const volCount = (positions.length - volStart) / 3;
		if (volCount < (NX + 1) * (NZ + 1) * 0.1) {
			positions.length = volStart;
			normals.length = volStart;
			randoms.length = volStart / 3;
			for (let ix = 0; ix <= NX; ix++) {
				deadline = await this._yieldIfOverBudget(deadline);
				for (let iz = 0; iz <= NZ; iz++) {
					const cx = bMin.x + (ix / NX) * bSize.x;
					const cz = bMin.z + (iz / NZ) * bSize.z;
					pushPt(cx, bMax.y, cz);
					if (ix === 0 || ix === NX || iz === 0 || iz === NZ) {
						for (let y = bMin.y; y < bMax.y - STEP * 0.5; y += STEP) pushPt(cx, y, cz);
					}
				}
			}
		}

		const nameLower = (mesh.name || "").toLowerCase();
		const haloSpread = nameLower.startsWith("scan1") || nameLower.startsWith("scan3") ? 0.5 : 0.42;
		const haloMargin = (haloSpread * (bSize.x + bSize.z)) / 2;
		const hxMin = bMin.x - haloMargin;
		const hxMax = bMax.x + haloMargin;
		const hzMin = bMin.z - haloMargin;
		const hzMax = bMax.z + haloMargin;
		const haloSizeX = hxMax - hxMin;
		const haloSizeZ = hzMax - hzMin;
		const STEP_H = STEP * 0.72;
		const HNX = Math.max(2, Math.round(haloSizeX / STEP_H));
		const HNZ = Math.max(2, Math.round(haloSizeZ / STEP_H));
		for (let ix = 0; ix <= HNX; ix++) {
			deadline = await this._yieldIfOverBudget(deadline);
			for (let iz = 0; iz <= HNZ; iz++) {
				const jx = (Math.random() - 0.5) * STEP * 0.9;
				const jz = (Math.random() - 0.5) * STEP * 0.9;
				const x = hxMin + (ix / HNX) * haloSizeX + jx;
				const z = hzMin + (iz / HNZ) * haloSizeZ + jz;
				if (x >= bMin.x && x <= bMax.x && z >= bMin.z && z <= bMax.z) continue;
				positions.push(x, bMin.y, z);
				normals.push(0, 1, 0);
				randoms.push(Math.random());
			}
		}

		// worldToLocal() recopie et inverse matrixWorld à chaque appel : sur les
		// ~280 000 points des trois bâtiments ça coûtait 27 ms au lieu de 2 ms.
		// L'inverse est constante ici (matrixWorld déjà à jour en haut de méthode).
		const toLocal = new THREE.Matrix4().copy(this.parallaxGroup2.matrixWorld).invert();

		const localPos = [];
		const localNorm = [];
		const tmpV = new THREE.Vector3();
		// performance.now() coûterait plus cher que le calcul lui-même s'il était
		// interrogé à chaque point : le budget est vérifié par paquets.
		let sinceCheck = 0;
		for (let i = 0; i < positions.length; i += 3) {
			if (++sinceCheck >= 4096) {
				sinceCheck = 0;
				deadline = await this._yieldIfOverBudget(deadline);
			}
			tmpV.set(positions[i], positions[i + 1], positions[i + 2]).applyMatrix4(toLocal);
			localPos.push(tmpV.x, tmpV.y, tmpV.z);
			localNorm.push(0, 1, 0);
		}

		const scanBbox = new THREE.Box3();
		const bMinL = new THREE.Vector3().copy(bMin).applyMatrix4(toLocal);
		const bMaxL = new THREE.Vector3().copy(bMax).applyMatrix4(toLocal);
		scanBbox.expandByPoint(bMinL);
		scanBbox.expandByPoint(bMaxL);
		const scanCenter = scanBbox.getCenter(new THREE.Vector3());

		// Allocation des Float32Array (~3 Mo) puis balayage du nuage pour le rayon
		// max : deux blocs de quelques ms, sortis de la tranche précédente.
		deadline = await this._yieldIfOverBudget(deadline);

		const scanGeom = new THREE.BufferGeometry();
		scanGeom.setAttribute("position", new THREE.Float32BufferAttribute(localPos, 3));
		scanGeom.setAttribute("normal", new THREE.Float32BufferAttribute(localNorm, 3));
		scanGeom.setAttribute("aRandom", new THREE.Float32BufferAttribute(randoms, 1));

		const uniforms = {
			uTime: { value: 0 },
			uRadius: { value: 0.0 },
			uMaxRadius: { value: 1.0 },
			uCenter: { value: new THREE.Vector3() },
			uBandWidth: { value: 0.15 },
			uPointSize: { value: POINT_SIZE },
			uBaseAlpha: { value: 0.0 },
			uBaseColor: { value: new THREE.Color("#00FCD9") },
			uScanColor: { value: new THREE.Color("#00FCD9") },
			uScreenHeight: { value: getPointScreenHeight() },
		};

		{
			const scanTopCenter = new THREE.Vector3(scanCenter.x, scanBbox.max.y, scanCenter.z);
			let topMaxR = 0;
			const tcX = scanTopCenter.x;
			const tcY = scanTopCenter.y;
			const tcZ = scanTopCenter.z;
			for (let i = 0; i < localPos.length; i += 3) {
				const dx = localPos[i] - tcX;
				const dy = localPos[i + 1] - tcY;
				const dz = localPos[i + 2] - tcZ;
				const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
				if (d > topMaxR) topMaxR = d;
			}
			uniforms.uCenter.value.copy(scanTopCenter);
			uniforms.uMaxRadius.value = topMaxR;
		}

		const scanMat = new THREE.ShaderMaterial({
			uniforms,
			vertexShader: `
				attribute float aRandom;
				uniform float uTime, uRadius;
				uniform vec3 uCenter;
				uniform float uBandWidth;
				uniform float uPointSize, uBaseAlpha;
				uniform float uScreenHeight;
				varying float vAlpha, vIntensity;
				void main() {
					vec3 p = position;
					vec3 pFloat = p;
					float wSpeed = 1.2 + 0.8 * aRandom;
					float w = uTime * wSpeed + aRandom * 6.28318;
					pFloat.x += 0.008 * sin(w);
					float dist = distance(p, uCenter);
					float d = uRadius - dist;
					float filled = smoothstep(-uBandWidth, uBandWidth, d);
					float crest  = exp(-d * d / (uBandWidth * uBandWidth * 0.5));
					vIntensity = clamp(filled + crest * 0.6, 0.0, 1.0);
					float flicker = 0.9 + 0.1 * sin(uTime * 2.0 + aRandom * 6.28318);
					float globalFade = smoothstep(0.0, uBandWidth, uRadius);
					vAlpha = clamp((uBaseAlpha + vIntensity) * flicker * globalFade, 0.0, 1.0);
					vec4 mvPos = modelViewMatrix * vec4(pFloat, 1.0);
					gl_PointSize = uPointSize * (uScreenHeight / -mvPos.z) * 0.003;
					gl_Position = projectionMatrix * mvPos;
				}
			`,
			fragmentShader: `
				uniform vec3 uBaseColor, uScanColor;
				varying float vAlpha, vIntensity;
				void main() {
					if (vAlpha < 0.01) discard;
					vec2 uv = gl_PointCoord - vec2(0.5);
					float r = length(uv);
					if (r > 0.5) discard;
					float light = pow(1.0 - r * 2.0, 1.5);
					vec3 col = mix(uBaseColor, uScanColor, vIntensity * vIntensity) * (0.8 + light * 0.6);
					float alpha = vAlpha * smoothstep(0.5, 0.3, r);
					gl_FragColor = vec4(col, alpha);
				}
			`,
			transparent: true,
			depthWrite: false,
		});

		const points = new THREE.Points(scanGeom, scanMat);
		points.frustumCulled = false;
		this.parallaxGroup2.add(points);

		const topY = Math.max(bMinL.y, bMaxL.y);
		const anchorLocal = new THREE.Vector3((bMinL.x + bMaxL.x) / 2, topY, (bMinL.z + bMaxL.z) / 2);

		return { name: mesh.name, mesh, points, mat: scanMat, uniforms, etiqLocalPos: anchorLocal };
	}

	_activateScene2() {
		this.topoUniforms2.uReveal.value = 1;
		this.topoUniforms2.uTopoRevealDist.value = 15.0;
		this.waveUniforms2.uTerrainReveal.value = 1;
		this.parallaxGroup2.rotation.set(0, 0, 0);
	}

	/* ====================== INTRO ====================== */
	startIntro() {
		this.introPlaying = true;
		this.introStart = performance.now();
		this.topoUniforms.uReveal.value = 0;
		this.topoUniforms.uTopoRevealDist.value = 0;
		this.waveUniforms.uTerrainReveal.value = 0;
		this.waveUniforms.uRevealRadius.value = -1.0;
		this.annotReveal = 0;
		this.hotspotReveal = 0;
		this.altitudePoints.forEach((pt) => {
			pt.alpha = 0;
			pt.lineProgress = 0;
		});
		this.camera.position.set(CAM_FINAL.x, CAM_START_Y, CAM_FINAL.z);
		this.housesGroup.traverse((c) => {
			if (c.isMesh && c.material) c.material.opacity = 0;
		});

		if (this.reducedMotion) {
			this._snapIntroToEnd();
		}
	}

	_snapIntroToEnd() {
		this.introPlaying = false;
		if (this.annotations.animStartMs === null) this.annotations.startCard1Cycle();
		this.camera.position.copy(CAM_FINAL);
		this.topoUniforms.uReveal.value = 1;
		this.topoUniforms.uTopoRevealDist.value = 15.0;
		this.waveUniforms.uTerrainReveal.value = 1;
		this.waveUniforms.uRevealRadius.value = 12.0;
		this.annotReveal = 1;
		this.hotspotReveal = 1;
		this.housesGroup.traverse((c) => {
			if (c.isMesh && c.material) c.material.opacity = 1;
		});
	}

	_updateIntro(elapsed) {
		if (!this.introPlaying) return;

		const camT = easeOut(phase(elapsed, 0.0, INTRO_DURATION));
		this.camera.position.x = CAM_FINAL.x;
		this.camera.position.y = CAM_START_Y + (CAM_FINAL.y - CAM_START_Y) * camT;
		this.camera.position.z = CAM_FINAL.z;

		const terrainT = easeOut(phase(elapsed, ...I_TERRAIN));
		this.topoUniforms.uReveal.value = terrainT;
		this.waveUniforms.uTerrainReveal.value = terrainT;

		const pointsT = easeInOut(phase(elapsed, I_TERRAIN[0] + 0.3, I_TERRAIN[1] + 0.4));
		this.waveUniforms.uRevealRadius.value = pointsT * 12.0 - 0.2;

		const topoT = easeInOut(phase(elapsed, ...I_TOPO_DRAW));
		this.topoUniforms.uTopoRevealDist.value = topoT * 15.0;

		const buildT = easeOut(phase(elapsed, ...I_BUILDINGS));
		this.housesGroup.traverse((c) => {
			if (c.isMesh && c.material) c.material.opacity = buildT;
		});

		this.annotReveal = phase(elapsed, ...I_ANNOT);
		this.hotspotReveal = easeInOut(phase(elapsed, 5.5, 6.5));

		if (elapsed > INTRO_DURATION) {
			this.introPlaying = false;
			this.annotations.startCard1Cycle();
			this.camera.position.copy(CAM_FINAL);
			this.topoUniforms.uReveal.value = 1;
			this.topoUniforms.uTopoRevealDist.value = 15.0;
			this.waveUniforms.uTerrainReveal.value = 1;
			this.waveUniforms.uRevealRadius.value = 12.0;
			this.annotReveal = 1;
			this.hotspotReveal = 1;
			this.housesGroup.traverse((c) => {
				if (c.isMesh && c.material) c.material.opacity = 1;
			});
		}
	}

	/* ====================== SCROLL & TRANSITION ====================== */
	_initScrollTargets() {
		const dir = this.hotspotWorldPos.clone().sub(CAM_FINAL).normalize();
		this.scrollCamEnd = CAM_FINAL.clone().addScaledVector(dir, 1.8);
		this.scrollCamEnd.y += 0.6;
		this.scrollTgtEnd = this.hotspotWorldPos.clone();
		this.scrollInitialized = true;
	}

	// Appelé par le contrôleur de scroll (GSAP ScrollTrigger), progress dans [0, 1]
	setScrollProgress(progress) {
		this.scrollT = Math.max(0, Math.min(1, progress)) * SCROLL_MAX;
	}

	_updateScrollAndTransition() {
		if (!this.scrollInitialized || this.introPlaying) return;

		this.scrollTSmooth += (this.scrollT - this.scrollTSmooth) * 0.07;
		const s = this.scrollTSmooth;

		const s1 = Math.min(s, 1) + Math.max(0, Math.min((s - 1) / 0.5, 1)) * 0.8;
		this.camera.position.lerpVectors(CAM_FINAL, this.scrollCamEnd, s1);
		this.controls.target.lerpVectors(new THREE.Vector3(-0.292, 0.285, 0.235), this.scrollTgtEnd, s1);
		this.scrollRotOffset = 0;

		const altFadeOut = 1 - Math.max(0, Math.min((s - 0.4) / 0.5, 1));
		this.annotReveal = altFadeOut;
		this.hotspotReveal = altFadeOut;

		// Filet de sécurité : la scène 2 est normalement déjà préchargée depuis la fin
		// de l'intro (cf. preloadScene2). Ne déclenche donc que si le préchargement a
		// échoué, ou n'a jamais pu démarrer (onglet ouvert en arrière-plan).
		const fadeOut = Math.max(0, Math.min((s - 0.7) / 0.4, 1));
		if (fadeOut > 0 && !this.scene2Loaded && !this.scene2Loading) this.preloadScene2();

		const fadeIn = Math.max(0, Math.min((s - 1.1) / 2.4, 1));

		const whiteFadeIn = s < 0.7 ? 0 : Math.max(0, Math.min((s - 0.7) / 0.4, 1));
		const whiteFadeOut = s < 1.1 ? 1 : Math.max(0, 1 - (s - 1.1) / 0.4);
		const whitePeak = s < 0.7 ? 0 : s < 1.1 ? whiteFadeIn : whiteFadeOut;
		if (this.whiteEl) this.whiteEl.style.opacity = whitePeak;

		this.canvas1.style.opacity = 1 - fadeOut;
		this.scene1Hidden = fadeOut >= 1;
		this.scene2FadeIn = fadeIn;
		this.annotations.canvas.style.opacity = this.scene2Loaded ? Math.max(1 - fadeOut, fadeIn) : 1 - fadeOut;

		this.canvas2.style.opacity = this.scene2Loaded ? fadeIn : 0;

		// Contenu éditorial de l'écran 1 (titre) : suit la scène 1.
		// En reduced-motion, le CSS (mode statique, sticky) reprend la main sans fondu JS.
		if (!this.reducedMotion) {
			if (this.screen1El) this.screen1El.style.opacity = 1 - fadeOut;
		}

		if (this.scene2Loaded) {
			const elTarget = 30.0;
			const eFade = easeInOutQuart(fadeIn);
			const elCurrent = S2_EL_START + (elTarget - S2_EL_START) * eFade;
			const azOffset = S2_AZ_OFFSET * (1 - eFade);
			const zoomMul = S2_ZOOM_START_MUL + (1 - S2_ZOOM_START_MUL) * eFade;
			this._applyOrthoWithEl(elCurrent, azOffset, zoomMul);
		}

		const nowScene2 = fadeIn >= 0.5 && this.scene2Loaded;
		if (nowScene2 !== this.scene2Active) {
			this.scene2Active = nowScene2;
			if (this.scene2Active) {
				if (this.scanItems2.length && this.scanAnimStartMs2 === null) {
					this.scan2Order = HomeBannerAnnotations.newShuffle(this.scanItems2.length);
					this.scan2OrderIdx = 0;
					this.activeScanIdx2 = this.scan2Order[0];
					this.lastScanIdx2 = null;
					this.scanAnimStartMs2 = performance.now();
					this.etiq2LocalPositions = [this.scanItems2[this.activeScanIdx2].etiqLocalPos];
					this.annotations.startCard2(1);
				}
			} else {
				this.scanAnimStartMs2 = null;
				this.activeScanIdx2 = null;
				this.annotations.resetCard2();
				this.scan2Order = null;
				this.scan2OrderIdx = 0;
				this.scanItems2.forEach((item) => {
					item.uniforms.uRadius.value = 0;
				});
			}
		}
	}

	/* ====================== BOUCLE DE RENDU ====================== */
	render() {
		const t = this.clock.getElapsedTime();
		this.annotations.clear();

		this.waveUniforms.uTime.value = t;
		this.parallaxGroup.rotation.y += (this.mx * 0.12 + this.scrollRotOffset - this.parallaxGroup.rotation.y) * 0.1;
		this.parallaxGroup.rotation.x += (this.my * 0.12 - this.parallaxGroup.rotation.x) * 0.1;
		this.controls.update();
		// Canvas 1 totalement effacé une fois la scène 2 affichée (cf. _updateScrollAndTransition) :
		// inutile de le rendre à chaque frame tant qu'il reste invisible.
		if (!this.scene1Hidden) this.renderer.render(this.scene, this.camera);

		if (this.introPlaying) {
			this._updateIntro((performance.now() - this.introStart) / 1000);
			this._updateAltWorldPositions();
			this.annotations.drawAltitudePoints(this.altitudePoints, this.camera, this.parallaxGroup, this.annotReveal, t);
			this.annotations.drawEtiquettes(this.etiquetteLocalPositions, this.camera, this.parallaxGroup, t);
		} else {
			this._updateScrollAndTransition();
			this._updateHotspotWorldPos();
			this._updateAltWorldPositions();
			if (!this.scene2Active && this.scene2FadeIn <= 0.01) {
				this.annotations.drawAltitudePoints(this.altitudePoints, this.camera, this.parallaxGroup, this.annotReveal, t);
				this.annotations.drawEtiquettes(this.etiquetteLocalPositions, this.camera, this.parallaxGroup, t);
			}
		}

		// La scène 2 est préchargée bien avant d'être vue : sans ce garde, le second
		// renderer WebGL et le cycle de scan tourneraient à chaque frame pendant toute
		// l'intro et tout le hero, sur un canvas à opacity 0.
		if (this.scene2Loaded && this.scene2FadeIn > 0) {
			this.waveUniforms2.uTime.value = t;
			const sweepCycle = 7.0;
			const sweepRange = 8.0;
			const sweepPhase = (t % sweepCycle) / sweepCycle;
			this.waveUniforms2.uSweepPos.value = -sweepRange / 2 + sweepPhase * sweepRange;

			if (this.scanItems2.length && this.activeScanIdx2 !== null) {
				const item = this.scanItems2[this.activeScanIdx2];
				item.uniforms.uTime.value = t;

				if (this.scanAnimStartMs2 !== null) {
					const elapsed = (performance.now() - this.scanAnimStartMs2) / 1000;
					const TRAVEL = this.scan2Speed;
					const PULSE = 0.35;
					const maxR = item.uniforms.uMaxRadius.value;

					if (elapsed < TRAVEL) {
						item.uniforms.uRadius.value = maxR * easeInOutQuad(elapsed / TRAVEL);
					} else if (elapsed < TRAVEL + PULSE) {
						const pt = (elapsed - TRAVEL) / PULSE;
						item.uniforms.uRadius.value = maxR * (1.0 + 0.18 * Math.sin(pt * Math.PI));
					} else if (elapsed < 2 * TRAVEL + PULSE) {
						const u = easeInOutQuad((elapsed - TRAVEL - PULSE) / TRAVEL);
						item.uniforms.uRadius.value = maxR * (1.0 - u);
					} else {
						item.uniforms.uRadius.value = 0;
					}

					if (elapsed >= 0.3 && this.annotations.animStartMs2 === null && this.etiq2LocalPositions.length > 0) {
						this.annotations.startCard2(1);
					}
				} else {
					item.uniforms.uRadius.value = 0;
				}

				if (this.annotations.isCard2CycleDone(2)) {
					this.annotations.resetCard2();
					this.lastScanIdx2 = this.activeScanIdx2;
					this.scan2OrderIdx++;
					if (!this.scan2Order || this.scan2OrderIdx >= this.scan2Order.length) {
						this.scan2Order = HomeBannerAnnotations.newShuffle(this.scanItems2.length, this.lastScanIdx2);
						this.scan2OrderIdx = 0;
					}
					this.activeScanIdx2 = this.scan2Order[this.scan2OrderIdx];
					this.etiq2LocalPositions = [this.scanItems2[this.activeScanIdx2].etiqLocalPos];
					this.scanAnimStartMs2 = performance.now();
				}
			}

			this.parallaxGroup2.rotation.y += (this.mx * 0.14 - this.parallaxGroup2.rotation.y) * 0.08;
			this.parallaxGroup2.rotation.x += (this.my * 0.1 - this.parallaxGroup2.rotation.x) * 0.08;
			this.renderer2.render(this.scene2, this.camera2);

			if (this.scene2FadeIn > 0.01 && this.etiq2LocalPositions.length) {
				this.annotations.drawEtiquette2(this.etiq2LocalPositions[0], this.camera2, this.parallaxGroup2, this.scene2FadeIn);
			}
		}
	}

	setMouse(clientX, clientY) {
		this.mx = (clientX / window.innerWidth - 0.5) * 0.4;
		this.my = (clientY / window.innerHeight - 0.5) * 0.4;
	}

	resize() {
		const pixelRatio = getPixelRatio();
		this.camera.aspect = window.innerWidth / window.innerHeight;
		this.camera.updateProjectionMatrix();
		this.renderer.setPixelRatio(pixelRatio);
		this.renderer2.setPixelRatio(pixelRatio);
		this.renderer.setSize(window.innerWidth, window.innerHeight);
		this.renderer2.setSize(window.innerWidth, window.innerHeight);
		const pointScreenHeight = getPointScreenHeight();
		this.waveUniforms.uScreenHeight.value = pointScreenHeight;
		this.waveUniforms2.uScreenHeight.value = pointScreenHeight;
		this.scanItems2.forEach((item) => {
			item.uniforms.uScreenHeight.value = pointScreenHeight;
		});
		this.annotations.resize(window.innerWidth, window.innerHeight);
		this._updateOrthoCamera();
	}
}
